<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">APPROACH</div>
      <textarea
        class="drawer-fieldTextarea"
        v-model="localApproach"
        placeholder="Describe how the agent should prospect these candidates..."
        rows="4"
        @blur="applyApproach"
      />
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">PERSONAS</div>
      <div v-if="localPersonas.length === 0" class="drawer-hint">
        No personas defined yet.
      </div>
      <div v-else class="personaList">
        <button
          v-for="(p, i) in localPersonas"
          :key="p.id || i"
          type="button"
          class="personaRow"
          @click="editPersona(i)"
        >
          <span class="personaInitials">{{ initials(p.name) }}</span>
          <div class="personaInfo">
            <span class="personaName">{{ p.name }}</span>
            <span v-if="p.explicit_titles?.length" class="personaTitles">
              {{ p.explicit_titles.join(', ') }}
            </span>
          </div>
        </button>
      </div>
      <button class="addPersonaBtn" type="button" @click="openCreateModal">
        + Add persona
      </button>
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">CONTACT LIMIT</div>
      <p class="drawer-hint">
        Skip prospecting an account once it already has this many active
        contacts in this play. Leave blank for no limit.
      </p>
      <input
        class="drawer-fieldInput"
        style="width: 100px"
        type="number"
        v-model.number="localMaxContactsPerAccount"
        min="1"
        placeholder="No limit"
        @blur="applyMaxContactsPerAccount"
      />
    </div>

    <div class="drawer-section">
      <div class="drawer-sectionLabel">CADENCE</div>
      <p class="drawer-hint">
        How often to re-run prospecting on a qualified account, by its
        status. Minimum 1 day.
      </p>
      <div class="drawer-field">
        <label class="drawer-fieldLabel">Active accounts (days)</label>
        <input
          class="drawer-fieldInput"
          style="width: 100px"
          type="number"
          v-model.number="localActiveDays"
          min="1"
          @blur="applyCadence"
        />
      </div>
      <div class="drawer-field">
        <label class="drawer-fieldLabel">Opportunistic accounts (days)</label>
        <input
          class="drawer-fieldInput"
          style="width: 100px"
          type="number"
          v-model.number="localOpportunisticDays"
          min="1"
          @blur="applyCadence"
        />
      </div>
    </div>
  </SalesPlayDrawerCard>

  <PersonaFormModal
    v-if="modalOpen"
    :persona="editingPersona"
    @close="modalOpen = false"
    @saved="onPersonaSaved"
    @deleted="onPersonaDeleted"
  />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import PersonaFormModal from './PersonaFormModal.vue'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'

interface PersonaEntry {
  id?: string
  name: string
  description?: string
  explicit_titles?: string[]
  titles_to_exclude?: string[]
}

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()

// ── Local working copy ──────────────────────────────────────────

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

const localApproach = ref(currentConfig().approach || '')
const localPersonas = ref<PersonaEntry[]>(
  (currentConfig().personas || []).map((p: any) => ({ ...p }))
)
const localMaxContactsPerAccount = ref<number | null>(currentConfig().max_contacts_per_account ?? null)
const localActiveDays = ref<number>(currentConfig().active_days || 1)
const localOpportunisticDays = ref<number>(currentConfig().opportunistic_days || 7)

function resetLocal() {
  const cfg = currentConfig()
  localApproach.value = cfg.approach || ''
  localPersonas.value = (cfg.personas || []).map((p: any) => ({ ...p }))
  localMaxContactsPerAccount.value = cfg.max_contacts_per_account ?? null
  localActiveDays.value = cfg.active_days || 1
  localOpportunisticDays.value = cfg.opportunistic_days || 7
}

watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// ── Persona modal ───────────────────────────────────────────────

const modalOpen = ref(false)
const editingPersona = ref<PersonaEntry | undefined>(undefined)
const editingIndex = ref<number | null>(null)

function openCreateModal() {
  editingPersona.value = undefined
  editingIndex.value = null
  modalOpen.value = true
}

function editPersona(index: number) {
  editingPersona.value = { ...localPersonas.value[index] }
  editingIndex.value = index
  modalOpen.value = true
}

function onPersonaSaved(persona: any) {
  const entry: PersonaEntry = {
    id: persona.id,
    name: persona.name,
    description: persona.description,
    explicit_titles: persona.explicit_titles || [],
    titles_to_exclude: persona.titles_to_exclude || [],
  }

  if (editingIndex.value !== null) {
    localPersonas.value[editingIndex.value] = entry
  } else {
    localPersonas.value.push(entry)
  }

  updateNodeConfig(props.nodeIndex, { personas: localPersonas.value })
  modalOpen.value = false
}

function onPersonaDeleted(personaId: string) {
  localPersonas.value = localPersonas.value.filter((p) => p.id !== personaId)
  updateNodeConfig(props.nodeIndex, { personas: localPersonas.value })
  modalOpen.value = false
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '·'
  return parts.slice(0, 2).map((s) => s[0].toUpperCase()).join('')
}

function applyApproach() {
  updateNodeConfig(props.nodeIndex, { approach: localApproach.value })
}

function applyMaxContactsPerAccount() {
  const value = localMaxContactsPerAccount.value
  localMaxContactsPerAccount.value = value && value > 0 ? Math.floor(value) : null
  updateNodeConfig(props.nodeIndex, { max_contacts_per_account: localMaxContactsPerAccount.value })
}

function applyCadence() {
  localActiveDays.value = Math.max(1, Math.floor(localActiveDays.value || 1))
  localOpportunisticDays.value = Math.max(1, Math.floor(localOpportunisticDays.value || 1))
  updateNodeConfig(props.nodeIndex, {
    active_days: localActiveDays.value,
    opportunistic_days: localOpportunisticDays.value,
  })
}
</script>

<style scoped>
.personaList {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
}
.personaRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: none;
  background: var(--color-bg-surface);
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: background var(--duration-fast) var(--ease-out);
}
.personaRow:hover {
  background: var(--color-bg-subtle);
}
.personaRow + .personaRow {
  border-top: 1px solid var(--color-border-subtle);
}
.personaInitials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: var(--color-bg-muted);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}
.personaInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.personaName {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}
.personaTitles {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.addPersonaBtn {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) 0;
  border: none;
  background: none;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-out);
}
.addPersonaBtn:hover {
  color: var(--color-text-primary);
}
</style>
